// The visibility control, shared by the single-post composer and the
// folder-import batch panel — the same shape TikTokPanel and YouTubePanel take,
// for the same reason: one setting whose meaning must read identically wherever
// it is set.
//
// The thing this component exists to say out loud: "visibility" is not one
// setting with one meaning. YouTube publishes with exactly the privacy status
// chosen here. Instagram and Facebook have no private or unlisted mode at all —
// they publish publicly whatever is chosen — and X has none either. A creator
// who picks "Private" and assumes nothing goes out publicly is wrong on three
// platforms out of five, so every surface that sets it renders `VisibilityNote`.
//
// In the batch flow there is deliberately NO default: an unanswered visibility
// is what silently made a whole imported queue private on YouTube.

import type { PlatformId, Visibility } from "../api";
import { InfoHint } from "./InfoHint";

/** Which flow the control is rendered in — only affects the wording. */
export type VisibilityScope = "single" | "batch";

/** The choice as the UI holds it. Deliberately allows `""` ("nothing chosen
 * yet"), which the wire shape (`Visibility`) cannot. */
export type VisibilityChoice = Visibility | "";

/** The only legal starting point for a batch: nothing chosen. */
export const EMPTY_VISIBILITY: VisibilityChoice = "";

/** Least-public first, so the safest option reads first. */
const VISIBILITY_OPTIONS: { value: Visibility; label: string }[] = [
  { value: "private", label: "Private" },
  { value: "unlisted", label: "Unlisted" },
  { value: "public", label: "Public" },
];

// Singular vs batch-wide phrasing. The strings differ only here so the two flows
// can't drift apart in meaning.
const COPY: Record<VisibilityScope, { label: string; placeholder: string }> = {
  single: {
    label: "Visibility",
    placeholder: "Select who can see this post",
  },
  batch: {
    label: "Visibility",
    placeholder: "Select who can see these posts",
  },
};

/** What (if anything) about the creator's choice blocks publishing. Shared so
 * the batch gate and any future single-post gate enforce the same rule. */
export function visibilityBlocker(
  choice: VisibilityChoice,
  scope: VisibilityScope,
): string | null {
  if (!choice) return `${COPY[scope].placeholder}.`;
  return null;
}

/** The per-platform reality, rendered wherever visibility is set. Kept next to
 * the control rather than in a help page: it is only useful at the moment the
 * choice is made. */
export function VisibilityNote() {
  return (
    <p className="muted small visibility-note">
      <strong>YouTube</strong> and <strong>TikTok</strong> honour this setting; TikTok uses the
      privacy level in its own post options. <strong>Instagram</strong> and{" "}
      <strong>Facebook</strong> always publish publicly — they have no private or unlisted
      setting, and neither does <strong>X</strong>.
    </p>
  );
}

// Instagram and Facebook have no private or unlisted mode at all — Meta
// publishes both PUBLICLY no matter what the visibility control above says.
const ALWAYS_PUBLIC_PLATFORMS: readonly PlatformId[] = ["instagram", "facebook"];

const ALWAYS_PUBLIC_LABEL: Partial<Record<PlatformId, string>> = {
  instagram: "Instagram",
  facebook: "Facebook",
};

/** Which of the always-public platforms are among `selected`, in a stable order.
 * Shared by the single-post and batch flows so the disclosure and any gate agree
 * on the exact set of platforms that ignore the visibility choice. */
export function alwaysPublicSelected(selected: Iterable<PlatformId>): PlatformId[] {
  const set = new Set(selected);
  return ALWAYS_PUBLIC_PLATFORMS.filter((id) => set.has(id));
}

/** The explicit, always-visible disclosure shown whenever Instagram or Facebook
 * is an actual destination: their posts go out PUBLICLY regardless of the
 * visibility control, because Meta gives neither a private nor an unlisted mode.
 *
 * Meta's Platform Terms require a person's consent before content is published
 * on their behalf. A creator who leaves Visibility on "Private" and selects
 * Instagram/Facebook would otherwise get public posts with no warning — the
 * weakest point in the flow. `VisibilityNote` states the per-platform reality in
 * muted help text; this states it again, unmuted and tied to the platforms
 * actually chosen, so it cannot be skimmed past at the one moment it matters. */
export function PublicOnlyNotice({ platforms }: { platforms: PlatformId[] }) {
  const names = platforms
    .map((id) => ALWAYS_PUBLIC_LABEL[id])
    .filter((name): name is string => Boolean(name));
  if (names.length === 0) return null;
  const subject = names.join(" and ");
  const areIs = names.length === 1 ? "is" : "are";
  const post = names.length === 1 ? "post" : "posts";
  return (
    <p className="notice visibility-public-notice" role="note">
      Heads up: your {subject} {post} {areIs} published{" "}
      <strong>publicly regardless of the visibility setting</strong> — Meta gives {subject} no
      private or unlisted mode.
    </p>
  );
}

export function VisibilityPicker({
  scope,
  value,
  onChange,
  disabled = false,
}: {
  scope: VisibilityScope;
  value: VisibilityChoice;
  onChange: (next: VisibilityChoice) => void;
  /** Freezes the control while a batch is mid-flight. */
  disabled?: boolean;
}) {
  const copy = COPY[scope];

  return (
    <label className="field">
      <span className="field-label-row">
        {copy.label}
        <InfoHint label="How visibility works per platform">
          <p>
            <strong>YouTube</strong> and <strong>TikTok</strong> honour this setting; TikTok uses
            the privacy level in its own post options. <strong>Instagram</strong> and{" "}
            <strong>Facebook</strong> always publish publicly — they have no private or unlisted
            setting, and neither does <strong>X</strong>.
          </p>
        </InfoHint>
      </span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as VisibilityChoice)}
      >
        {/* Unselectable, so the batch can start with nothing chosen without that
            state ever being something the creator could pick on purpose. */}
        <option value="" disabled>
          {copy.placeholder}
        </option>
        {VISIBILITY_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}
